/**
 * Livello offline-first a livello HTTP (come Workbox NetworkFirst + BackgroundSync), attivo solo nell'app nativa
 * (o con il flag di collaudo `localStorage.ml_offline_layer = "1"`): sul web normale il comportamento NON cambia.
 *
 *  LETTURE (PostgREST GET/HEAD, /auth/v1/user)
 *    rete → ok: risposta + copia su disco · rete assente/lenta/errore: ultima copia buona (con l'ora dell'aggiornamento)
 *    Nessuna schermata resta vuota: gli hook non vedono errori ma i dati dell'ultima sincronizzazione.
 *  SCRITTURE (tabelle operative in lista)
 *    online → invio normale · offline/errore di rete → in coda durevole, risposta ottimistica con l'id generato qui,
 *    le liste mostrano subito la riga (sovrapposizione), reinvio automatico al ritorno della rete.
 *  TUTTO IL RESTO (rpc, storage, funzioni, tabelle non in lista): passa com'è; se manca la rete fallisce subito (niente attese).
 */
import { Capacitor } from "@capacitor/core";
import { idbAll, idbDelete, idbGet, idbPut, idbClear } from "./idb";
import { isOnlineNow, reportNetworkFailure, reportNetworkSuccess } from "./net";
import { enqueue, pendingForTable, uuid, type QueuedWrite } from "./writeQueue";

// archivio locale e schema: moduli pesanti, caricati solo quando serve (mai sul web normale)
let localMod: Promise<typeof import("./localdb")> | null = null;
const loadLocal = () => (localMod ??= import("./localdb"));
let schemaMod: Promise<typeof import("./schema.generated")> | null = null;
const loadSchema = () => (schemaMod ??= import("./schema.generated"));
async function answerFromMirror(...args: Parameters<typeof import("./localdb").answerFromMirror>) {
  return (await loadLocal()).answerFromMirror(...args);
}

export const OFFLINE_LAYER_FLAG = "ml_offline_layer";
export const NEEDS_NETWORK_EVENT = "ml:needs-network";

function signalNeedsNetwork() {
  try {
    window.dispatchEvent(new CustomEvent(NEEDS_NETWORK_EVENT));
  } catch {
    /* ambiente senza window */
  }
}

export function offlineLayerEnabled(): boolean {
  try {
    if (Capacitor.isNativePlatform()) return true;
    return typeof localStorage !== "undefined" && localStorage.getItem(OFFLINE_LAYER_FLAG) === "1";
  } catch {
    return false;
  }
}

/** Tabelle su cui si può operare senza rete (operatività di cantiere). Le altre richiedono la rete. */
export const OFFLINE_WRITE_TABLES: ReadonlySet<string> = new Set([
  // cantiere / giornaliero
  "diario_cantiere", "foto_diario", "issue_commessa", "sicurezza_cantiere", "precheck_giornalieri",
  "presenze_cantiere", "presenze_diario", "assenze_personale", "work_progress_logs",
  // risorse
  "personale_cantiere", "mezzi_attrezzature", "utilizzo_mezzi", "manutenzioni_mezzi", "mezzi_attivita",
  "mezzi_attivita_distribuzione", "risorse_attivita", "risorse_attivita_distribuzione", "squadre_cantiere",
  "squadre_membri", "squadre_mezzi", "squadre_commesse_assegnazioni", "commessa_personale_ruoli",
  // pianificazione lavori (Gantt)
  "attivita_gantt", "dipendenze_gantt", "attivita_voci", "materiali_attivita",
  // magazzino / DDT
  "ddt_cantiere", "righe_ddt_cantiere", "movimenti_magazzino", "materiali_magazzino",
  // commessa
  "commesse", "costi_commessa", "documenti_commessa", "varianti",
  // acquisti e subappalti
  "fornitori", "ordini_fornitore", "righe_ordine", "consegne_ordine",
  "subappalti", "subappalto_avanzamenti", "subappalto_avanzamento_righe", "subappalto_documenti",
]);

/**
 * Tabelle che NON si scrivono offline: il server è l'unica fonte di verità (fatturazione e numerazione legale,
 * permessi, configurazione) oppure hanno già una coda dedicata (rapportini, timbrature).
 */
const WRITE_DENY: ReadonlySet<string> = new Set([
  "tenants", "user_roles", "user_submodule_permissions", "tenant_modules", "tenant_document_types", "tenant_fiscal_profile",
  "profiles", "iva_codes", "vat_rates", "invoices", "invoice_lines", "invoice_installments", "invoice_installments_payments",
  "invoice_ddt_links", "invoice_ddt_scostamenti", "autofatture", "autofatture_righe", "fatture_cassetto_fiscale",
  "righe_fattura_cassetto", "scostamenti_fattura_ddt", "rapportini_cantiere", "rapportino_manodopera", "rapportino_materiali",
  "rapportino_mezzi", "rapportino_lavorazioni", "rapportino_eventi", "rapportino_foto", "rapportini_operaio", "timbrature_eventi",
  "timbrature_config",
]);
/** Si può registrare offline (coda durevole + invio automatico)? Tutte le tabelle dell'app tranne le escluse. */
export async function canQueueWrite(table: string): Promise<boolean> {
  if (WRITE_DENY.has(table)) return false;
  if (OFFLINE_WRITE_TABLES.has(table)) return true;
  return !!(await loadSchema()).OFFLINE_TABLES[table];
}

const GET_TIMEOUT_MS = 8000;
const WRITE_TIMEOUT_MS = 15000;
const MAX_CACHED_BODY_CHARS = 8_000_000;
const MAX_ENTRIES = 3000;
const LAST_UID_KEY = "ml_last_uid";

interface CacheEntry {
  k: string;
  body: string;
  status: number;
  headers: Record<string, string>;
  savedAt: number;
  /** per l'aggiornamento silenzioso in background */
  uid?: string;
  url?: string;
  method?: string;
  req?: Record<string, string>;
  reqBody?: string;
}

let writesSincePrune = 0;
/** rete «degradata» (lenta): finché dura si risponde dalla copia e si aggiorna in background */
let degradedUntil = 0;

function isNetErr(e: unknown): boolean {
  if (!e) return false;
  const err = e as { name?: string; message?: string };
  if ((e as { __timeout?: boolean }).__timeout) return true;
  if (err.name === "TypeError") return true;
  return /failed to fetch|networkerror|network request failed|load failed|timed out|timeout|offline|bad gateway/i.test(String(err.message ?? e));
}

function jwtSub(auth: string): string | null {
  try {
    const t = auth.replace(/^Bearer\s+/i, "");
    const part = t.split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.sub === "string" ? json.sub : null;
  } catch {
    return null;
  }
}

function rememberUid(uid: string) {
  try {
    localStorage.setItem(LAST_UID_KEY, uid);
  } catch {
    /* ignora */
  }
}
function lastUid(): string | null {
  try {
    return localStorage.getItem(LAST_UID_KEY);
  } catch {
    return null;
  }
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit | undefined, ms: number): Promise<Response> {
  const ctrl = new AbortController();
  let timedOut = false;
  const external = init?.signal;
  const onAbort = () => ctrl.abort();
  if (external) {
    if (external.aborted) ctrl.abort();
    else external.addEventListener("abort", onAbort, { once: true });
  }
  const t = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, ms);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } catch (e) {
    if (timedOut) {
      const te = new TypeError("Failed to fetch (timeout)");
      (te as unknown as { __timeout: boolean }).__timeout = true;
      throw te;
    }
    throw e;
  } finally {
    clearTimeout(t);
    external?.removeEventListener("abort", onAbort);
  }
}

// ---------------------------------------------------------------------------
// Cache letture
// ---------------------------------------------------------------------------

async function cacheGet(key: string): Promise<CacheEntry | undefined> {
  return idbGet<CacheEntry>("http", key);
}

function cachePut(
  key: string,
  body: string,
  status: number,
  headers: Record<string, string>,
  meta?: { uid: string; url: string; method: string; req: Record<string, string>; reqBody?: string },
) {
  if (body.length > MAX_CACHED_BODY_CHARS) return;
  void idbPut("http", { k: key, body, status, headers, savedAt: Date.now(), ...(meta ?? {}) }).then(() => {
    writesSincePrune++;
    if (writesSincePrune >= 150) {
      writesSincePrune = 0;
      void pruneCache();
    }
  });
}

export async function pruneCache(): Promise<void> {
  const all = await idbAll<CacheEntry>("http");
  const limite = Date.now() - 30 * 24 * 3600 * 1000;
  const scadute = all.filter((e) => e.savedAt < limite);
  for (const e of scadute) await idbDelete("http", e.k);
  const restanti = all.filter((e) => e.savedAt >= limite).sort((a, b) => a.savedAt - b.savedAt);
  if (restanti.length > MAX_ENTRIES) {
    for (const e of restanti.slice(0, restanti.length - MAX_ENTRIES + 500)) await idbDelete("http", e.k);
  }
}

/** Logout: niente dati dell'utente precedente sul dispositivo. */
export async function clearOfflineCache(): Promise<void> {
  await idbClear("http");
  try {
    await (await loadLocal()).clearMirror();
  } catch {
    /* ignora */
  }
  try {
    localStorage.removeItem(LAST_UID_KEY);
  } catch {
    /* ignora */
  }
}

export async function lastSyncTime(): Promise<number | null> {
  const v = await idbGet<{ k: string; t: number }>("meta", "last_sync");
  return v?.t ?? null;
}
function markSync() {
  void idbPut("meta", { k: "last_sync", t: Date.now() });
}

function cacheKeyFor(uid: string, method: string, url: string, headers: Headers, body = ""): string {
  const pick = (h: string) => headers.get(h) ?? "";
  return [uid, method, url, pick("accept"), pick("prefer"), pick("range"), pick("accept-profile"), body].join("|");
}

/** RPC di sola lettura (get_/is_/has_/list_…) memorizzabili come le letture; mai quelle che assegnano numeri o creano. */
const RPC_LETTURA = /^(get_|is_|has_|list_|fn_get_|calc_|compute_|search_|check_|can_|stats_|summary_|report_)/;
const RPC_ESCLUSE = /(next|nuov|genera|assegn|create|increment|reserve|allocate|numer|sequence|insert|update|delete|set_|add_|remove_)/;
/** RPC di sola lettura con nome non standard (verificate a mano sul codice). */
const RPC_LETTURA_ESPLICITE = new Set(["mia_persona", "has_role", "has_submodule_access", "timbrature_giorno_minimo", "get_user_tenant", "is_platform_admin"]);
/** RPC di servizio: offline falliscono in silenzio (nessun avviso «serve la rete»). */
const RPC_SILENZIOSE = new Set(["server_now", "refresh_stati_sicurezza_cantiere", "demo_reset_stato"]);
function rpcName(name: string): string {
  return name.replace(/^rpc\//, "").split("?")[0];
}
function isReadRpc(name: string): boolean {
  const n = rpcName(name);
  return RPC_LETTURA_ESPLICITE.has(n) || (RPC_LETTURA.test(n) && !RPC_ESCLUSE.test(n));
}

function fromCache(entry: CacheEntry): Response {
  const h = new Headers(entry.headers);
  h.set("x-ml-cache", "hit");
  h.set("x-ml-cached-at", String(entry.savedAt));
  return new Response(entry.status === 204 ? null : entry.body, { status: entry.status, headers: h });
}

// ---------------------------------------------------------------------------
// Sovrapposizione delle scritture in coda sulle letture
// ---------------------------------------------------------------------------

function parseFilters(url: string): Map<string, string> {
  const m = new Map<string, string>();
  try {
    new URL(url).searchParams.forEach((v, k) => {
      if (!["select", "order", "limit", "offset", "on_conflict", "columns"].includes(k)) m.set(k, v);
    });
  } catch {
    /* url non valido */
  }
  return m;
}

function rowMatchesSimpleFilters(row: Record<string, unknown>, filters: Map<string, string>): boolean {
  for (const [col, expr] of filters) {
    if (!(col in row)) continue;
    const v = row[col];
    if (expr.startsWith("eq.")) {
      if (String(v) !== expr.slice(3)) return false;
    }
  }
  return true;
}

function applyOverlay(table: string, url: string, accept: string, text: string): string {
  const ops = pendingForTable(table);
  if (ops.length === 0) return text;
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return text;
  }
  const single = /vnd\.pgrst\.object\+json/.test(accept);
  const filters = parseFilters(url);
  let rows: Record<string, unknown>[] = Array.isArray(json) ? (json as Record<string, unknown>[]) : json && typeof json === "object" ? [json as Record<string, unknown>] : [];
  if (!rows.every((r) => r && typeof r === "object")) return text;
  for (const op of ops) {
    if (op.op === "insert" && op.rows) {
      for (const r of op.rows) {
        if (rows.some((x) => x.id && x.id === r.id)) continue;
        if (rowMatchesSimpleFilters(r, filters)) rows.push({ ...r });
      }
    } else if (op.op === "update" && op.patch) {
      rows = rows.map((r) => (r.id && op.rowIds.includes(String(r.id)) ? { ...r, ...op.patch } : r));
    } else if (op.op === "delete") {
      rows = rows.filter((r) => !(r.id && op.rowIds.includes(String(r.id))));
    }
  }
  if (single) return JSON.stringify(rows[0] ?? json);
  return JSON.stringify(rows);
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

function parseIdsFromFilter(url: string): string[] {
  try {
    const sp = new URL(url).searchParams;
    const id = sp.get("id");
    if (!id) return [];
    if (id.startsWith("eq.")) return [id.slice(3)];
    const m = id.match(/^in\.\((.*)\)$/);
    if (m) return m[1].split(",").map((s) => s.replace(/^"|"$/g, "").trim()).filter(Boolean);
  } catch {
    /* ignora */
  }
  return [];
}

export function createOfflineFetch(cfg: { backendUrl: string }): typeof fetch {
  const REST = `${cfg.backendUrl}/rest/v1/`;
  const AUTH_USER = `${cfg.backendUrl}/auth/v1/user`;
  const AUTH = `${cfg.backendUrl}/auth/v1/`;

  return async function offlineFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url;
    const method = (init?.method ?? (typeof input === "object" && !(input instanceof URL) ? (input as Request).method : "GET")).toUpperCase();
    const isRest = url.startsWith(REST);
    const isAuthUser = url.startsWith(AUTH_USER) && method === "GET";
    const isAuth = url.startsWith(AUTH);

    if (!isRest && !isAuthUser) {
      // altre richieste: se sappiamo di essere offline falliscono subito; l'auth ha un timeout per non appendere l'app
      if (!isOnlineNow() && (isAuth || url.startsWith(cfg.backendUrl))) {
        if (!isAuth && method !== "GET" && method !== "HEAD") signalNeedsNetwork();
        throw new TypeError("Failed to fetch (offline)");
      }
      if (isAuth) {
        try {
          const r = await fetchWithTimeout(input, init, 10_000);
          reportNetworkSuccess();
          return r;
        } catch (e) {
          if (isNetErr(e)) reportNetworkFailure();
          throw e;
        }
      }
      return fetch(input, init);
    }

    const headers = new Headers(init?.headers ?? (typeof input === "object" && !(input instanceof URL) ? (input as Request).headers : undefined));
    const subFromToken = jwtSub(headers.get("authorization") ?? "");
    if (subFromToken) rememberUid(subFromToken);
    const path = isRest ? url.slice(REST.length) : "";
    const table = path.split("?")[0];
    const isRpc = table.startsWith("rpc/");

    // ------------------------------------------------------------ LETTURE
    const rpcLettura = isRpc && method === "POST" && isReadRpc(table);
    if (method === "GET" || method === "HEAD" || rpcLettura) {
      const uid = subFromToken ?? (isOnlineNow() ? null : lastUid());
      const bodyKey = rpcLettura && typeof init?.body === "string" ? init.body : "";
      const key = uid ? cacheKeyFor(uid, method, url, headers, bodyKey) : null;
      const serve = async (): Promise<Response | null> => {
        if (!key) return null;
        const hit = await cacheGet(key);
        if (!hit) return null;
        const body = isRest && !isRpc && hit.status === 200 ? applyOverlay(table, url, headers.get("accept") ?? "", hit.body) : hit.body;
        return fromCache({ ...hit, body });
      };

      // archivio locale per tabelle: risponde anche a ciò che non è mai stato aperto
      const serveLocal = async (): Promise<Response | null> => {
        if (!uid || !isRest || isRpc) return null;
        const a = await answerFromMirror({
          uid,
          url,
          restBase: REST,
          method,
          accept: headers.get("accept") ?? "",
          prefer: headers.get("prefer") ?? "",
        });
        if (!a) return null;
        const body =
          a.body != null && a.status === 200 ? applyOverlay(table, url, headers.get("accept") ?? "", a.body) : a.body;
        const h = new Headers(a.headers);
        h.set("x-ml-cache", "local");
        h.set("x-ml-cached-at", String(a.at));
        return new Response(a.status === 204 ? null : body, { status: a.status, headers: h });
      };
      // tra la copia esatta e l'archivio locale vince quella aggiornata più di recente
      const serveAny = async (): Promise<Response | null> => {
        const hit = await serve();
        const local = await serveLocal();
        if (hit && local) {
          return Number(local.headers.get("x-ml-cached-at")) > Number(hit.headers.get("x-ml-cached-at")) ? local : hit;
        }
        return hit ?? local;
      };

      if (!isOnlineNow()) {
        const hit = await serveAny();
        if (hit) return hit;
        throw new TypeError("Failed to fetch (offline, nessuna copia salvata)");
      }
      // rete → cache: se esiste una copia e la rete è lenta (> 3 s) si risponde subito con la copia,
      // lasciando che la richiesta finisca in background e aggiorni la copia (stale-while-revalidate).
      const network = async (): Promise<Response> => {
        const t0 = Date.now();
        const res = await fetchWithTimeout(input, init, GET_TIMEOUT_MS);
        if (res.status >= 502 && res.status <= 504) throw new TypeError("Bad gateway");
        reportNetworkSuccess();
        if (Date.now() - t0 < 1500) degradedUntil = 0; // la rete è tornata veloce
        if (res.ok && key) {
          const clone = res.clone();
          void clone.text().then((body) => {
            const h: Record<string, string> = {};
            for (const n of ["content-type", "content-range"]) {
              const v = res.headers.get(n);
              if (v) h[n] = v;
            }
            const req: Record<string, string> = {};
            for (const n of ["accept", "prefer", "range", "accept-profile", "content-type"]) {
              const v = headers.get(n);
              if (v) req[n] = v;
            }
            cachePut(key, body, res.status, h, uid ? { uid, url, method, req, reqBody: bodyKey || undefined } : undefined);
            markSync();
          });
        }
        if (res.ok && isRest && !isRpc && pendingForTable(table).length > 0) {
          const text = await res.text();
          const merged = applyOverlay(table, url, headers.get("accept") ?? "", text);
          const h = new Headers(res.headers);
          h.delete("content-length");
          return new Response(merged, { status: res.status, headers: h });
        }
        return res;
      };

      const hasCopy = key ? !!(await cacheGet(key)) : false;
      const netP = network();
      try {
        const degraded = Date.now() < degradedUntil;
        if (!hasCopy) {
          if (!uid || !isRest || isRpc) return await netP;
          // mai vista questa richiesta: se la rete è lenta risponde l'archivio locale
          const w = await Promise.race([
            netP.then((r) => ({ r })),
            new Promise<{ slow: true }>((resolve) => setTimeout(() => resolve({ slow: true }), degraded ? 0 : 2500)),
          ]);
          if ("r" in w) return w.r;
          const local = await serveLocal();
          if (local) {
            degradedUntil = Date.now() + 30_000;
            netP.catch((e) => {
              if (isNetErr(e)) reportNetworkFailure();
            });
            return local;
          }
          return await netP;
        }
        const winner = await Promise.race([
          netP.then((r) => ({ r })),
          new Promise<{ slow: true }>((resolve) => setTimeout(() => resolve({ slow: true }), degraded ? 0 : 2500)),
        ]);
        if ("r" in winner) return winner.r;
        // lenta: servi la copia e lascia proseguire la rete in background
        degradedUntil = Date.now() + 30_000;
        netP.catch((e) => {
          if (isNetErr(e)) reportNetworkFailure();
        });
        const hit = await serveAny();
        if (hit) return hit;
        return await netP;
      } catch (e) {
        if (!isNetErr(e)) throw e;
        reportNetworkFailure();
        const hit = await serveAny();
        if (hit) return hit;
        throw e;
      }
    }

    // ------------------------------------------------------------ SCRITTURE in lista
    if (isRest && !isRpc && (method === "POST" || method === "PATCH" || method === "DELETE") && (await canQueueWrite(table))) {
      let body = typeof init?.body === "string" ? init.body : null;
      let rows: Record<string, unknown>[] | undefined;
      let patch: Record<string, unknown> | undefined;
      let rowIds: string[] = [];
      let op: QueuedWrite["op"] = "insert";

      if (method === "POST") {
        // l'id nasce sul dispositivo: invio e reinvio sono idempotenti
        try {
          const parsed = body ? JSON.parse(body) : null;
          const arr: Record<string, unknown>[] = Array.isArray(parsed) ? parsed : parsed && typeof parsed === "object" ? [parsed] : [];
          const isUpsert = !!headers.get("prefer")?.includes("resolution=");
          // corpo anomalo, oppure upsert senza id (non si può rendere idempotente): serve la rete
          if (arr.length === 0 || (isUpsert && arr.some((r) => !r.id))) {
            return passthroughWrite(input, init);
          }
          for (const r of arr) if (!r.id) r.id = uuid();
          rows = arr;
          rowIds = arr.map((r) => String(r.id));
          body = JSON.stringify(Array.isArray(parsed) ? arr : arr[0]);
          op = "insert";
        } catch {
          return passthroughWrite(input, init);
        }
      } else if (method === "PATCH") {
        rowIds = parseIdsFromFilter(url);
        if (rowIds.length === 0) rowIds = await idsFromLocal(url, subFromToken ?? lastUid());
        if (rowIds.length === 0) return passthroughWrite(input, init); // filtro non risolvibile in locale: serve la rete
        try {
          patch = body ? JSON.parse(body) : undefined;
        } catch {
          return passthroughWrite(input, init);
        }
        op = "update";
      } else {
        rowIds = parseIdsFromFilter(url);
        if (rowIds.length === 0) rowIds = await idsFromLocal(url, subFromToken ?? lastUid());
        if (rowIds.length === 0) return passthroughWrite(input, init);
        op = "delete";
      }

      const sendInit: RequestInit = { ...init, body: body ?? undefined };
      if (isOnlineNow()) {
        try {
          const res = await fetchWithTimeout(url, sendInit, WRITE_TIMEOUT_MS);
          if (res.status >= 502 && res.status <= 504) throw new TypeError("Bad gateway");
          reportNetworkSuccess();
          return res;
        } catch (e) {
          if (!isNetErr(e)) throw e;
          reportNetworkFailure();
        }
      }

      const keep: Record<string, string> = {};
      for (const n of ["content-type", "prefer", "accept", "content-profile", "accept-profile"]) {
        const v = headers.get(n);
        if (v) keep[n] = v;
      }
      await enqueue({
        uid: subFromToken ?? lastUid(),
        table,
        method: method as QueuedWrite["method"],
        path,
        headers: keep,
        body,
        op,
        rowIds,
        rows,
        patch,
      });
      return optimisticResponse(method, headers, op, rows, rowIds, patch);
    }

    return passthroughWrite(input, init);
  };

  /** Righe interessate da un aggiornamento/eliminazione «a filtro», ricavate dall'archivio locale. */
  async function idsFromLocal(url: string, uid: string | null): Promise<string[]> {
    if (!uid) return [];
    try {
      const u = new URL(url);
      ["select", "order", "limit", "offset"].forEach((k) => u.searchParams.delete(k));
      u.searchParams.set("select", "id");
      const a = await answerFromMirror({ uid, url: u.toString(), restBase: REST, method: "GET", accept: "", prefer: "" });
      if (!a || a.status !== 200 || !a.body) return [];
      const rows = JSON.parse(a.body) as { id?: string }[];
      return rows.map((r) => r.id).filter((x): x is string => typeof x === "string");
    } catch {
      return [];
    }
  }

  function passthroughWrite(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (!isOnlineNow()) {
      let silent = false;
      try {
        const u = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url;
        const rel = u.replace(cfg.backendUrl, "");
        console.debug("[offline] richiede la rete:", (init?.method ?? "GET").toUpperCase(), rel);
        const m = rel.match(/\/rest\/v1\/rpc\/([a-z_0-9]+)/);
        silent = !!m && RPC_SILENZIOSE.has(m[1]);
      } catch {
        /* solo diagnostica */
      }
      if (!silent) signalNeedsNetwork();
      return Promise.reject(new TypeError("Failed to fetch (offline: questa azione richiede la rete)"));
    }
    return fetchWithTimeout(input, init, WRITE_TIMEOUT_MS)
      .then((r) => {
        reportNetworkSuccess();
        return r;
      })
      .catch((e) => {
        if (isNetErr(e)) reportNetworkFailure();
        throw e;
      });
  }
}

function optimisticResponse(
  method: string,
  headers: Headers,
  op: QueuedWrite["op"],
  rows: Record<string, unknown>[] | undefined,
  rowIds: string[],
  patch: Record<string, unknown> | undefined,
): Response {
  const prefer = headers.get("prefer") ?? "";
  const wantsRep = prefer.includes("return=representation");
  const single = /vnd\.pgrst\.object\+json/.test(headers.get("accept") ?? "");
  const h = { "content-type": "application/json; charset=utf-8", "x-ml-queued": "1" };
  if (!wantsRep) return new Response(null, { status: method === "POST" ? 201 : 204, headers: { "x-ml-queued": "1" } });
  let payload: unknown[];
  if (op === "insert") payload = rows ?? [];
  else if (op === "update") payload = rowIds.map((id) => ({ id, ...(patch ?? {}) }));
  else payload = rowIds.map((id) => ({ id }));
  return new Response(JSON.stringify(single ? payload[0] ?? null : payload), { status: method === "POST" ? 201 : 200, headers: h });
}

// ---------------------------------------------------------------------------
// Statistiche e aggiornamento silenzioso
// ---------------------------------------------------------------------------

export async function cacheStats(): Promise<{ entries: number; bytes: number; oldest: number | null }> {
  const all = await idbAll<CacheEntry>("http");
  let bytes = 0;
  let oldest: number | null = null;
  for (const e of all) {
    bytes += e.body.length;
    if (oldest == null || e.savedAt < oldest) oldest = e.savedAt;
  }
  return { entries: all.length, bytes, oldest };
}

/**
 * Aggiorna in background (senza toccare l'interfaccia) tutte le letture già salvate più vecchie di `olderThanMs`:
 * mantiene «calda» la copia offline di ciò che l'utente usa davvero. Concorrenza limitata, interrompibile.
 */
export async function refreshKnownUrls(opts: {
  uid: string;
  token: string;
  apiKey: string;
  olderThanMs: number;
  maxEntries?: number;
  /** solo le letture non tabellari (rpc): le tabelle le copia l'archivio locale */
  onlyRpc?: boolean;
  shouldStop?: () => boolean;
}): Promise<number> {
  const all = (await idbAll<CacheEntry>("http"))
    .filter((e) => e.uid === opts.uid && e.url && Date.now() - e.savedAt > opts.olderThanMs && (!opts.onlyRpc || /\/rest\/v1\/rpc\//.test(e.url)))
    .sort((a, b) => a.savedAt - b.savedAt)
    .slice(0, opts.maxEntries ?? 400);
  let done = 0;
  let i = 0;
  const worker = async () => {
    while (i < all.length) {
      if (opts.shouldStop?.() || !isOnlineNow()) return;
      const e = all[i++];
      try {
        const res = await fetchWithTimeout(
          e.url as string,
          {
            method: e.method ?? "GET",
            headers: { ...(e.req ?? {}), apikey: opts.apiKey, Authorization: `Bearer ${opts.token}` },
            body: e.reqBody,
          },
          12_000,
        );
        if (res.ok) {
          const body = await res.text();
          cachePut(e.k, body, res.status, e.headers, { uid: opts.uid, url: e.url as string, method: e.method ?? "GET", req: e.req ?? {}, reqBody: e.reqBody });
          done++;
        }
      } catch {
        if (!isOnlineNow()) return;
      }
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);
  if (done > 0) markSync();
  return done;
}
